import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Box,
  Text,
  Link,
  Button,
  Avatar,
  Input,
  FormLabel,
  Image,
  List,
  Spinner,
  ListItem,
} from '@chakra-ui/react'
import { formatUrl } from '@/components/video-page/utils/formatOptions'
import { getInitials } from '../avatar'
import {
  ChannelData,
  useUpdateChannelMutation,
} from '@/src/mutations/my-channel/update-user'
import { useChannelDetailsQuery } from '@/src/queries/my-channel/channel-details'
import { toast } from 'sonner'
import { useCallback, useEffect, useRef, useState } from 'react'
import { clearInputFile } from '@/src/lib/utils'
import { useProfile } from '@/src/store/profile-store'
import { useUserMutation } from '@/src/queries/user/user-details'
import { useRouter } from 'next/navigation'
import { format } from 'path'
import { useQueryClient } from '@tanstack/react-query'
import { profile } from 'console'
import { debounce } from 'lodash'
import { ErrorMessages, ErrorResponse } from '@/src/types/api-error'
import { useSuggestChannelNamesMutation } from '@/src/mutations/auth/channel-name'

const inputStyles = {
  size: {
    base: 'md',
  },
  rounded: 'md',
  border: '1px solid #E5E5E5',
  px: {
    base: '3',
    md: '4',
  },
  fontSize: {
    base: 'sm',
    md: 'md',
  },
}

export default function EditChannel({
  isOpen,
  onClose,
  name,
  themeColor,
  channelShareName,
  accountFlow,
}: {
  isOpen: boolean
  onClose: () => void
  themeColor: string
  name: string
  imageUrl: string
  channelShareName: string
  accountFlow: boolean
}) {
  const updateChannel = useUpdateChannelMutation()
  const { data: channelDetails } = useChannelDetailsQuery(channelShareName)
  const { setTempIconFile, setTempCoverIconFile } = useProfile()
  const getDefaultValues = useCallback(
    () =>
      ({
        channelName: channelDetails?.channelName || '',
        coverUrl: channelDetails?.coverUrl || '',
        imageUrl: channelDetails?.imageUrl || '',
        waterMarkImage: channelDetails?.waterMarkImage || '',
        bio: channelDetails?.bio || '',
        instagramLink: channelDetails?.instagramLink || '',
        facebookLink: channelDetails?.facebookLink || '',
        emailLink: channelDetails?.emailLink || '',
        twitterLink: channelDetails?.twitterLink || '',
        websiteLink: channelDetails?.websiteLink || '',
      }) as ChannelData,
    [channelDetails]
  )

  const [formValues, setFormValues] = useState<ChannelData>(getDefaultValues())
  const [error, setError] = useState<string | null>(null)
  const [errorFile, setErrorFile] = useState<any>({ profile: '', cover: '' })

  useEffect(() => {
    setFormValues(getDefaultValues())
  }, [channelDetails, getDefaultValues])

  const updateFormValues = (key: keyof ChannelData, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }))
  }

  const [suggestions, setSuggestions] = useState<string[]>([])
  const [fieldRequiredError, setFieldRequiredError] = useState(false)
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const profilePicRef = useRef<HTMLInputElement>(null)
  const bannerRef = useRef<HTMLInputElement>(null)
  const errorRef = useRef<HTMLDivElement>(null)
  const userMutation = useUserMutation()
  const handlePictureChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setTempFile: (file: File) => void,
    inputRef: React.RefObject<HTMLInputElement>,
    value: 'imageUrl' | 'coverUrl'
  ) => {
    const file = e.target.files?.[0]
    if (file) {
      const fileSizeInBytes = file.size
      const fileSizeInKb = fileSizeInBytes / 1024
      if (fileSizeInKb > 4096 && value === 'imageUrl') {
        setErrorFile({
          ...errorFile,
          profile: 'Please select file less than 4MB.',
        })
      } else if (fileSizeInKb > 6144 && value === 'coverUrl') {
        setErrorFile({
          ...errorFile,
          cover: 'Please select file less than 6MB.',
        })
      } else if (fileSizeInKb <= 4096 && value === 'imageUrl') {
        setErrorFile({ ...errorFile, profile: '' })
      } else if (fileSizeInKb <= 6144 && value === 'coverUrl') {
        setErrorFile({ ...errorFile, cover: '' })
      }
      setTempFile(file)
      const tempIconURL = URL.createObjectURL(file)
      updateFormValues(value, tempIconURL)
      inputRef.current && clearInputFile(inputRef.current)
    }
  }
  const clearProfilePicture = () => {
    setTempIconFile(null)
    updateFormValues('imageUrl', '')
    setErrorFile({ ...errorFile, profile: '' })
  }
  const clearCoverPicture = () => {
    setTempCoverIconFile(null)
    updateFormValues('coverUrl', '')
    setErrorFile({ ...errorFile, cover: '' })
  }

  const checkValuesChanged = () => {
    return Object.keys(formValues).some(
      (key) =>
        formValues[key as keyof ChannelData] !==
        channelDetails?.[key as keyof ChannelData]
    )
  }

  const queryClient = useQueryClient()

  function handleSubmit() {
    setError(null)
    if (!checkValuesChanged()) {
      return onClose()
    }
    updateChannel.mutate(formValues, {
      onSuccess: () => {
        userMutation.mutate(undefined, {
          onSuccess: (user) => {
            if (accountFlow) {
              queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
              queryClient.invalidateQueries({ queryKey: ['channel-details'] })
              toast.success('Profile updated successfully!')
              onClose()
              return
            }
            toast.success('Channel updated successfully!')
            const newChannelShareName = user.channelShareName
            onClose()
            // router.replace('/c/' + newChannelShareName)
            window.location.replace('/c/' + newChannelShareName)
          },
        })
      },
      onError: (error) => {
        toast.error(error.message)
        setError(error.message)
        errorRef.current?.scrollIntoView({ behavior: 'smooth' })
      },
    })
  }

  const { mutate: suggestChannelNames, isPending } =
    useSuggestChannelNamesMutation()

  const handleInputChange = debounce((value: string) => {
    if (value.trim()) {
      setFieldRequiredError(false)
      suggestChannelNames(
        { channelName: value },
        {
          onSuccess: (data: string[]) => {
            setSuggestions(data)
            setDropdownVisible(true)
            setError(null)
          },
          onError: () => {
            setError('error/channelName-already-exists')
            setSuggestions([])
            setDropdownVisible(false)
          },
        }
      )
    } else {
      setSuggestions([])
      setDropdownVisible(false)
      setError(null)
      setFieldRequiredError(true)
    }
  }, 300)

  const handleSelectSuggestion = (suggestion: string) => {
    // setValue('publicChannelName', suggestion)
    updateFormValues('channelName', suggestion)
    setDropdownVisible(false)
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prevIndex) =>
        prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
      )
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[highlightedIndex])
      }
    } else if (e.key === 'Tab') {
    }
  }

  useEffect(() => {
    if (isDropdownVisible) {
      document.addEventListener('keydown', handleKeyDown)
    } else {
      document.removeEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [highlightedIndex, isDropdownVisible])

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setDropdownVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={['full', 'full', '3xl']}
      isCentered={false}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent margin="auto">
        <ModalHeader>Edit {accountFlow ? 'Profile' : 'Channel'}</ModalHeader>
        <ModalCloseButton mt={2} />
        <hr />

        <>
          <ModalBody pt={4}>
            {!accountFlow && (
              <>
                <Box ref={errorRef}>
                  {error && (
                    <Text color="red" fontSize="md" mb={2}>
                      <Text as="span" fontWeight={500}>
                        Error:
                      </Text>{' '}
                      {error}
                    </Text>
                  )}
                </Box>
                <Box mb={5}>
                  <Text
                    fontSize={{
                      base: 'sm',
                      md: 'md',
                    }}
                    fontWeight="600"
                    marginBottom="5px"
                    color="#525252"
                  >
                    Public Channel Name&nbsp;
                    <Text as={'span'} color="lyk-red">
                      *
                    </Text>
                  </Text>
                  <Input
                    value={formValues.channelName}
                    required
                    maxLength={50}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[a-zA-Z0-9\s]*$/.test(value)) {
                        updateFormValues('channelName', value);
                        handleInputChange(value);
                      }
                    }}
                    {...inputStyles}
                    onKeyDown={handleKeyDown}
                  />

                  {isDropdownVisible && suggestions.length > 0 && (
                    <Box
                      my={2}
                      border="1px solid #ccc"
                      borderRadius="md"
                      ref={suggestionsRef}
                      backgroundColor="white"
                      width={'100%'}
                      boxShadow={'lg'}
                    >
                      <List borderRadius="md">
                        {isPending ? (
                          <Spinner size="sm" alignSelf={'center'} />
                        ) : (
                          suggestions.map((suggestion, index) => (
                            <ListItem
                              key={suggestion}
                              onClick={() => handleSelectSuggestion(suggestion)}
                              cursor="pointer"
                              fontSize={14}
                              borderRadius="md"
                              color={'#7A7A7A'}
                              fontWeight={500}
                              p={2}
                              _hover={{ backgroundColor: '#F4F4F4' }}
                              backgroundColor={
                                highlightedIndex === index ? '#F4F4F4' : 'white'
                              }
                            >
                              {suggestion}
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Box>
                  )}

                  {(error === 'error/channelName-already-exists' ||
                    fieldRequiredError) && (
                    <Text
                      className={`mt-2 text-sm ${error === 'error/channelName-already-exists' ? 'text-[#ED837F]' : 'text-[#ED837F]'}`}
                    >
                      {error === 'error/channelName-already-exists'
                        ? 'This channel name is already taken'
                        : 'Channel name is required'}
                    </Text>
                  )}
                  <Text
                    fontWeight={500}
                    fontSize={14}
                    color={'#3e3e3e'}
                    opacity={0.5}
                    mt={1}
                  >
                    Your channel currently appears as{' '}
                    <Link
                      href={`https://www.lykstage.com/c/${channelShareName}`}
                      target="_blank"
                    >
                      lykstage.com/c/{channelShareName}
                    </Link>
                  </Text>
                </Box>
              </>
            )}
            <Box mb={5}>
              <Box>
                <Text
                  fontSize={{
                    base: 'sm',
                    md: 'md',
                  }}
                  fontWeight="600"
                  marginBottom="5px"
                  color="#525252"
                >
                  Profile Picture
                </Text>
                <Text
                  color={'#3e3e3e'}
                  opacity={0.5}
                  fontSize={'sm'}
                  fontWeight={500}
                >
                  Your profile picture will appear throughout LYK Stage
                </Text>
              </Box>
              <Flex
                flexDir={['column', 'column', 'row']}
                align={'center'}
                gap={'1.5em'}
                mt={3}
              >
                <Box>
                  <Avatar
                    name={name}
                    src={formValues.imageUrl}
                    getInitials={getInitials}
                    size={['2xl', '2xl', '2xl']}
                    bg={themeColor}
                    color="white"
                  />
                </Box>
                <Box>
                  <Text
                    fontWeight={500}
                    fontSize={14}
                    color={'#3e3e3e'}
                    opacity={0.5}
                  >
                    It’s recommended to use a picture that’s at least 98 x 98
                    pixels and 4MB or less. Use a PNG (not transparent) or JPEG
                    file. Make sure your picture follows the LYKSTAGE Community
                    Guidelines.
                  </Text>
                  <Flex flexDir={['column', 'column', 'row']} mt={3}>
                    <Button
                      as={FormLabel}
                      cursor={'pointer'}
                      backgroundColor={'lyk-green'}
                      color={'white'}
                      _hover={{ opacity: 0.6 }}
                      fontSize={'sm'}
                      w={['100%', '100%', 'auto']}
                    >
                      Upload Profile Picture
                      <Input
                        ref={profilePicRef}
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        display="none"
                        onChange={(e) =>
                          handlePictureChange(
                            e,
                            setTempIconFile,
                            profilePicRef,
                            'imageUrl'
                          )
                        }
                      />
                    </Button>
                    <Button
                      backgroundColor={'lyk-red'}
                      color={'white'}
                      _hover={{ opacity: 0.6 }}
                      fontSize={'sm'}
                      w={['100%', '100%', 'auto']}
                      isDisabled={
                        updateChannel.isPending || formValues.imageUrl === ''
                      }
                      onClick={clearProfilePicture}
                    >
                      Remove current
                    </Button>
                  </Flex>
                  <Text color="red" fontSize="md" mb={2}>
                    {errorFile?.profile}
                  </Text>
                </Box>
              </Flex>
            </Box>
            {!accountFlow && (
              <>
                <Box mb={5}>
                  <Box>
                    <Text
                      fontSize={{
                        base: 'sm',
                        md: 'md',
                      }}
                      fontWeight="600"
                      marginBottom="5px"
                      color="#525252"
                    >
                      Banner Image
                    </Text>
                    <Text
                      color={'#3e3e3e'}
                      opacity={0.5}
                      fontSize={'sm'}
                      fontWeight={500}
                    >
                      This image will appear across the top of your channel
                    </Text>
                  </Box>
                  <Flex
                    flexDir={['column', 'column', 'row']}
                    align={['center', 'center', 'flex-start']}
                    gap={'1.5em'}
                    mt={3}
                  >
                    <Box
                      border="2px dashed #D9D9D9"
                      rounded="lg"
                      aspectRatio={16 / 9}
                      flex={0.35}
                      p={2}
                    >
                      <Image
                        fallbackSrc={'/my-channel/fallback.png'}
                        src={formValues.coverUrl}
                        h="40%"
                        w="100%"
                        objectFit="cover"
                        objectPosition={'center'}
                        alt=""
                        rounded={'lg'}
                      />
                    </Box>
                    <Box flex={0.65}>
                      <Text
                        fontWeight={500}
                        fontSize={14}
                        color={'#3e3e3e'}
                        opacity={0.5}
                      >
                        For the best results on all devices, use an image that’s
                        at least 2048 x 1152 pixels and 6MB or less. Use a PNG
                        (not transparent) or JPEG file.
                      </Text>
                      <Flex
                        flexDir={['column', 'column', 'row']}
                        mt={3}
                        gap={0}
                      >
                        <Button
                          as={FormLabel}
                          cursor={'pointer'}
                          backgroundColor={'lyk-green'}
                          color={'white'}
                          _hover={{ opacity: 0.6 }}
                          fontSize={'sm'}
                          w={['100%', '100%', 'auto']}
                        >
                          Upload Banner Image
                          <Input
                            ref={bannerRef}
                            type="file"
                            accept="image/png, image/jpeg, image/jpg"
                            display="none"
                            onChange={(e) =>
                              handlePictureChange(
                                e,
                                setTempCoverIconFile,
                                bannerRef,
                                'coverUrl'
                              )
                            }
                          />
                        </Button>
                        <Button
                          backgroundColor={'lyk-red'}
                          color={'white'}
                          _hover={{ opacity: 0.6 }}
                          fontSize={'sm'}
                          w={['100%', '100%', 'auto']}
                          isDisabled={
                            updateChannel.isPending ||
                            formValues.coverUrl === ''
                          }
                          onClick={clearCoverPicture}
                        >
                          Remove current
                        </Button>
                      </Flex>
                      <Text color="red" fontSize="md" mb={2}>
                        {errorFile?.cover}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </>
            )}
          </ModalBody>
        </>

        <ModalFooter
          borderTop={'solid'}
          borderColor={'#eaeaea'}
          bg={'white'}
          position={'sticky'}
          bottom={0}
          gap={'1em'}
        >
          <Button
            variant={'ghost'}
            onClick={() => {
              setTempCoverIconFile(null)
              setTempIconFile(null)
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button
            backgroundColor={'lyk-green'}
            color={'white'}
            _hover={{ opacity: 0.6 }}
            onClick={handleSubmit}
            isLoading={updateChannel.isPending || userMutation.isPending}
            px={10}
            isDisabled={errorFile?.profile || errorFile?.cover}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
